import React, { useContext } from "react";
import { getDurationDisplay, getTZAbbreviation, requireBookingDataFields } from "../../../../utils";
import { gap, marginLeft, marginRight, timeFormats, typography } from "../../../styles";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BookingData } from "@src/App/AppDataProvider/types";
import { PartRequired } from "@src/App/utils/types";
import { addMinutes } from "date-fns";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { AppContext } from "../../../../AppDataProvider/AppDataProvider";
import CustomerTranslations from "../../../../utils/CustomerTranslations";
type BookingInfoBookingData = PartRequired<BookingData, "organisation">;
const SummaryItem = styled.div`
  display: flex;
  align-items: center;

  ${typography.small}
  /*
   * BookingInfo renders inside ProgressTracker's CollapsibleSection body
   * (a plain, uncoloured div — only the section's *title* explicitly sets
   * --d-text), so without an explicit colour here this text fell back to
   * the browser's default black in every theme, including dark brands
   * where it's invisible against --d-surface/--d-card. Explicit --d-text
   * keeps it theme-reactive like the rest of the app.
   */
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};

  > *:not(:last-child) {
    ${marginRight.s}
  }
`;
const DurationItem = styled.div`
  display: flex;
  align-items: center;

  ${gap.s}
  ${typography.small}
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;
const SlotDateTime = styled.div`
  display: flex;
  flex-direction: column;
`;
const SlotTime = styled.div`
  display: flex;
`;
const TimezoneText = styled.div`
  ${marginLeft.s}
  color: ${({
  theme
}) => theme.brand.cssVars["--d-muted"]};
`;
const DurationLabel = styled.label`
  text-transform: capitalize;
  color: ${({
  theme
}) => theme.brand.cssVars["--d-muted"]};
`;
const Occurrences = styled.div`
  ${typography.smallBold}
  color: ${({
  theme
}) => theme.brand.cssVars["--d-accent"]};
`;
function BookingInfo() {
  const {
    i18n,
    t
  } = useTranslation();
  const {
    translateEntity
  } = CustomerTranslations.useCustomerTranslation();
  const {
    appData
  } = useContext(AppContext);
  const {
    bookingData
  } = appData;
  requireBookingDataFields<BookingInfoBookingData>(bookingData, ["organisation"]);
  const {
    duration,
    organisation,
    recurringBooking,
    resource,
    selectedBookings,
    timezone,
    startDate
  } = bookingData;

  // If booking onto an existing booking where the booking time has been changed and its duration no longer matches duration on service, then use start and end time of booking to calculate duration
  let calculatedDuration = duration;
  if (selectedBookings && selectedBookings.length === 1) {
    const [selectedBooking] = selectedBookings;
    const {
      start,
      end
    } = selectedBooking;
    const startTime = new Date(start);
    const endTime = new Date(end);
    const durationInMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);
    calculatedDuration = {
      minutes: durationInMinutes
    };
  }
  function getTimeSlotDisplay() {
    if (!startDate) return null;
    const timezoneAbbreviation = getTZAbbreviation({
      date: startDate,
      locale: i18n.language,
      timezone
    });
    return <SummaryItem data-sentry-element="SummaryItem" data-sentry-component="getTimeSlotDisplay" data-sentry-source-file="BookingInfo.tsx">
        <SlotDateTime data-sentry-element="SlotDateTime" data-sentry-source-file="BookingInfo.tsx">
          <div>
            {startDate.toLocaleDateString(i18n.language, /* eslint-disable sort-keys */{
            weekday: "short",
            day: "numeric",
            month: "short",
            year: "numeric",
            timeZone: timezone
          } /* eslint-enable sort-keys */)}
          </div>
          {calculatedDuration ? <SlotTime>
              {startDate.toLocaleString(i18n.language, {
            ...timeFormats.default,
            timeZone: timezone
          })}
              {" – "}
              {addMinutes(startDate, calculatedDuration.minutes).toLocaleTimeString(i18n.language, {
            ...timeFormats.default,
            timeZone: timezone
          })}
              <TimezoneText>{timezoneAbbreviation}</TimezoneText>
            </SlotTime> : null}
        </SlotDateTime>
      </SummaryItem>;
  }
  function getRecurrenceDisplay() {
    if (!recurringBooking || !selectedBookings || !startDate) return null;
    const selectedOccurrences = selectedBookings.length;
    return <SummaryItem data-sentry-element="SummaryItem" data-sentry-component="getRecurrenceDisplay" data-sentry-source-file="BookingInfo.tsx">
        <Occurrences data-sentry-element="Occurrences" data-sentry-source-file="BookingInfo.tsx">{selectedOccurrences}</Occurrences>
        <FontAwesomeIcon icon={faSync} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="BookingInfo.tsx" />
        <div>
          {t("fromValue", {
          value: startDate.toLocaleDateString(i18n.language, /* eslint-disable sort-keys */{
            weekday: "short",
            day: "numeric",
            month: "short",
            year: "numeric"
          } /* eslint-enable sort-keys */)
        })}
        </div>
      </SummaryItem>;
  }
  return <div data-sentry-component="BookingInfo" data-sentry-source-file="BookingInfo.tsx">
      <SummaryItem data-sentry-element="SummaryItem" data-sentry-source-file="BookingInfo.tsx">
        {translateEntity({
        document: organisation,
        type: "account"
      }).name}
      </SummaryItem>
      {resource ? <SummaryItem>
          {translateEntity({
        document: resource,
        type: "resource"
      }).name}
        </SummaryItem> : null}
      {getTimeSlotDisplay()}
      {calculatedDuration ? <DurationItem>
          <DurationLabel>{t("duration", {
          count: 1
        })}</DurationLabel>
          <span>{getDurationDisplay(calculatedDuration, t)}</span>
        </DurationItem> : null}
      {getRecurrenceDisplay()}
    </div>;
}
export default BookingInfo;